import clsx from 'clsx';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import Collapse from '@mui/material/Collapse';
import Divider from './Divider';

import SidebarListItem from './SidebarListItem';

import { getSidebarNodes } from 'store/modules/graph/graph';
import { setIsPopoverOpen } from 'store/modules/nodeEditor';

import styles from './styles.scss';

// Remove or Add uri to the expandedNodes depending on if it already existed
const toggleSidebarNode = (expandedNodes, uri) => {
  if (expandedNodes.includes(uri)) {
    return expandedNodes.filter(n => n !== uri);
  } else {
    return [...expandedNodes, uri];
  }
};

// Iterate over matching classes (resultCountsPerClass) from graph search results,
// find matching class and returns how many entities it contains.
const numberOfEntityMatches = (graphSearchResults, uri) => {
  return graphSearchResults.entities?.resultCountsPerClass?.find(c => c.uri === uri)?.resultCount || 0;
};

/**
 * Nodes are matched if:
 *  1) They were matched themselves
 *  2) There is an entity matched of this class and this node is not just a reference to a parent node
 */
const isNodeMatched = (graphSearchResults, uri, isRefNode, subClassNodes) => {
  return (
    graphSearchResults.literals?.some(literal => literal.uri === uri) ||
    graphSearchResults.measures?.some(measure => measure.uri === uri) ||
    graphSearchResults.classes?.some(classItem => classItem.uri === uri) ||
    subClassNodes?.some(node => node.subClassOf === uri) ||
    // we only want to match entities for the original node that contains these entities, not everywhere there is a ref node
    (numberOfEntityMatches(graphSearchResults, uri) > 0 && !isRefNode)
  );
};

const searchSidebarNodes = (graphSearchResults, sidebarNodes) => {
  // Refs are children nodes that are just references to another parent node, i.e. a relationship to a class (that is already a parent node).
  // E.g. Orders can have Customers as a child node, but if there is a separate parent node Customers, then Orders.Customers is a ref node.
  // Returns: an array containing all children that are refNodes;
  const refNodes = sidebarNodes.flatMap(node =>
    node.children.filter(child => sidebarNodes.some(node => node.uri === child.uri))
  );

  const subClassNodes = sidebarNodes.flatMap(node =>
    node.children.filter(child => sidebarNodes.some(node => node.uri === child.subClassOf))
  );

  return sidebarNodes
    .map(node => {
      const matchedChildren = node.children.filter(child => {
        const childIsRefNode = refNodes.some(ref => ref.uri === child.uri);
        const isChildMatched = isNodeMatched(graphSearchResults, child.uri, childIsRefNode, subClassNodes);
        if (isChildMatched) return isChildMatched;
      });
      if (matchedChildren.length > 0 || isNodeMatched(graphSearchResults, node.uri, false)) {
        return { ...node, children: matchedChildren };
      }
      return null;
    })
    .filter(n => n !== null);
};

const SidebarList = ({
  sidebarNodeStructure,
  nodes,
  graphSearchResults,
  openNode,
  unsetHash,
  openChildNode,
  isEmbedded,
  showData,
  t
}) => {
  const [didInitializeExpandedNodes, setDidInitializedExpandedNodes] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState([]);

  const dispatch = useDispatch();

  const hoverDelayTimeoutRef = useRef(null);

  const hasSearchResults = useMemo(() => !!graphSearchResults.isFetching, [graphSearchResults.isFetching]);
  const searchTerm = useMemo(() => graphSearchResults.query || '', [graphSearchResults.query]);

  const handleSidebarListItemClick = useCallback(uri => showData(uri), [showData]);
  const onExpandClick = useCallback(uri => setExpandedNodes(prev => toggleSidebarNode(prev, uri)), []);
  const onShowDataClick = useCallback(uri => handleSidebarListItemClick(uri), [handleSidebarListItemClick]);

  // When we hover over a list item, we save a state informing that its popover is open.
  // Also, if the timeout from "disablePopverDelay" is running, we cancel it here.
  // The "clearTimeout" is necessary because when hovering over many items, we hover in/out
  // many items at once. It prevents the timeout from being called more than once.
  const enablePopoverDelay = () => {
    clearTimeout(hoverDelayTimeoutRef.current);
    dispatch(setIsPopoverOpen(true));
  };

  // When we hover out of a list item, we call a timeout with a small delay, in case we hover back in.
  // If we don't do this, the popover will always be closed immediately, including when transitioning between items.
  const disablePopoverDelay = () => {
    hoverDelayTimeoutRef.current = setTimeout(() => {
      dispatch(setIsPopoverOpen(false));
    }, 400);
  };

  const renderSidebarNode = useCallback(
    (node, children, subClassChildren, level, index) => {
      // We are expanded if the user actively expanded or whenever we're searching
      const isExpanded = expandedNodes.includes(node.uri) || hasSearchResults;

      return (
        <Fragment key={`node_${index}_${node.uri}`}>
          <SidebarListItem
            uniqueId={`parent_${index}_${node.uri}`}
            sidebarNode={node}
            onExpandClick={() => onExpandClick(node.uri)}
            onShowDataClick={() => onShowDataClick(node.uri)}
            isExpanded={isExpanded}
            level={level}
            isExpandable={children.length > 0}
            isEmbedded={isEmbedded}
            searchTerm={searchTerm}
            numberOfEntityMatches={numberOfEntityMatches(graphSearchResults, node.uri)}
            forceOpen={node.uri === openNode}
            // unset hash when popover opens, except for the one we force to be open
            onOpened={() => {
              enablePopoverDelay();
              return node.uri === openNode ? null : unsetHash;
            }}
            // unset hash when popover closes
            onClosed={() => {
              disablePopoverDelay();
              return unsetHash;
            }}
          />
          <Collapse in={isExpanded} timeout="auto">
            {children?.map((child, childIndex) => (
              <SidebarListItem
                uniqueId={`child_${index}_${childIndex}_${node.uri}`}
                key={`child_${childIndex}_${node.uri}`}
                sidebarNode={child}
                level={level + 1}
                isExpandable={false}
                onExpandClick={() => onExpandClick(node.uri)}
                onShowDataClick={() => onShowDataClick(node.uri)}
                isEmbedded={isEmbedded}
                searchTerm={searchTerm}
                numberOfEntityMatches={numberOfEntityMatches(graphSearchResults, child.uri)}
                forceOpen={child.uri === openChildNode}
                // unset hash when popover opens, except for the one we force to be open
                onOpened={() => {
                  enablePopoverDelay();
                  return node.uri === openChildNode ? null : unsetHash;
                }}
                // unset hash when popover closes
                onClosed={() => {
                  disablePopoverDelay();
                  return unsetHash;
                }}
              />
            ))}
            {/* for now there we only support one level of subClasses which might change in the future */}
            {subClassChildren?.map((subClassNode, subClassNodeIndex) =>
              renderSidebarNode(subClassNode, subClassNode.children, null, level + 1, subClassNodeIndex)
            )}
          </Collapse>
        </Fragment>
      );
    },
    [
      isEmbedded,
      expandedNodes,
      graphSearchResults,
      hasSearchResults,
      searchTerm,
      openNode,
      unsetHash,
      openChildNode,
      onExpandClick,
      onShowDataClick
    ]
  );

  // This is the sidebar node tree structure that we want to render, without filters applied;
  const sidebarNodes = useMemo(() => getSidebarNodes(nodes, sidebarNodeStructure), [nodes, sidebarNodeStructure]);

  const nodesFilteredBySearch = useMemo(() => {
    return hasSearchResults ? searchSidebarNodes(graphSearchResults, sidebarNodes) : sidebarNodes;
  }, [hasSearchResults, graphSearchResults, sidebarNodes]);

  // separate subClass nodes from nodes without subclasses
  const { subClassNodes, nonSubClassNodes } = useMemo(() => {
    const subClassNodes = [];
    const nonSubClassNodes = [];
    nodesFilteredBySearch.forEach(node => {
      if (node.subClassOf) {
        subClassNodes.push(node);
      } else {
        nonSubClassNodes.push(node);
      }
    });
    return { subClassNodes, nonSubClassNodes };
  }, [nodesFilteredBySearch]);

  // Design decision to always expand the first two parents on initial load (if e.g. only one exists, doesn't matter)
  useEffect(() => {
    if (!didInitializeExpandedNodes && nonSubClassNodes.length > 0) {
      setDidInitializedExpandedNodes(true);
      setExpandedNodes(nonSubClassNodes.slice(0, 2).map(node => node.uri));
    }
  }, [nonSubClassNodes, didInitializeExpandedNodes]);

  return (
    <div>
      <ul className={styles.kgSidebarList}>
        {nonSubClassNodes.map((node, index) => {
          const childrenWithoutSubClasses = [];
          const subClassChildren = [];

          // Separate subClass children from children without subclasses
          node.children.forEach(child => {
            const potentialSubClassNode = subClassNodes.find(subClassNode => subClassNode.uri === child.uri);
            if (potentialSubClassNode) {
              subClassChildren.push(potentialSubClassNode);
            } else {
              childrenWithoutSubClasses.push(child);
            }
          });

          return (
            <Fragment key={`node_${index}_${node.uri}`}>
              {renderSidebarNode(node, childrenWithoutSubClasses, subClassChildren, 0, index)}
              <Divider />
            </Fragment>
          );
        })}
        {nodesFilteredBySearch.length === 0 && (
          <li className={clsx(styles.kgSidebarItem, styles.kgSidebarParentItem)}>
            <div className={clsx(styles.kgSidebarText, styles.kgSidebarParentItemText)}>
              <span className={styles.ellipsisSpan}>{t('no-results')}</span>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
};

export default memo(SidebarList);
